import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="post-harvest--new-containers"
// Manages steps that consume their containers and create new ones (destemming, trimming):
// new container rows, budget against the consumed input weight, and serialization.
export default class extends Controller {
  static targets = [
    "outputList",
    "template",
    "budgetDisplay",
    "outputContainersDataField",
  ];
  static values = {
    existingIdentifiers: Array,
    inputWeight: Number,
    inputUnit: String,
    unitConversions: Object,
    identifierPrefix: String,
    photoKey: String,
  };

  connect() {
    this.formElement = this.element.closest("form");
    this.beforeSubmitHandler = this.handleSubmit.bind(this);
    if (this.formElement) {
      this.formElement.addEventListener("submit", this.beforeSubmitHandler);
    }
    this.update();
  }

  disconnect() {
    if (this.formElement) {
      this.formElement.removeEventListener("submit", this.beforeSubmitHandler);
    }
  }

  handleSubmit(event) {
    this.serialize();
    if (this.overBudget()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  update() {
    this.updateBudget();
    this.serialize();
  }

  addOutput(event) {
    event.preventDefault();
    const content = this.templateTarget.content.firstElementChild.outerHTML
      .replace(/NEW_ID/g, Date.now())
      .replace(/NEW-XX/g, this.nextAvailableIdentifier());
    this.outputListTarget.insertAdjacentHTML("beforeend", content);
    this.update();
  }

  removeOutput(event) {
    event.preventDefault();
    const row = event.target.closest("[data-output-row]");
    if (row) {
      row.remove();
      this.update();
    }
  }

  // Sums output rows in the input unit so the budget compares like with like.
  usedWeight() {
    let used = 0;
    this.outputRows().forEach((row) => {
      const value =
        parseFloat(row.querySelector("[data-field=net_weight_value]")?.value) ||
        0;
      const unit =
        row.querySelector("[data-field=net_weight_unit]")?.value || "g";
      used += this.toGrams(value, unit) / this.toGrams(1, this.inputUnit());
    });
    return used;
  }

  overBudget() {
    return this.usedWeight() > this.inputWeightValue + 0.005;
  }

  updateBudget() {
    if (!this.hasBudgetDisplayTarget) return;

    this.budgetDisplayTarget.textContent = `${this.usedWeight().toFixed(2)} / ${this.inputWeightValue.toFixed(2)} ${this.inputUnit()}`;
    const overBudget = this.overBudget();
    this.budgetDisplayTarget.classList.toggle("text-error", overBudget);
    this.budgetDisplayTarget.classList.toggle("font-bold", overBudget);
  }

  inputUnit() {
    return this.inputUnitValue || "g";
  }

  toGrams(value, unit) {
    return value * (this.unitConversionsValue[unit] || 1);
  }

  nextAvailableIdentifier() {
    const usedIdentifiers = new Set([
      ...this.existingIdentifiersValue.map((id) => this.normalizeId(id)),
      ...this.currentOutputIdentifiers(),
    ]);

    let n = 1;
    while (true) {
      const candidate = `${this.identifierPrefixValue}-${String(n).padStart(2, "0")}`;
      if (!usedIdentifiers.has(this.normalizeId(candidate))) return candidate;
      n++;
    }
  }

  currentOutputIdentifiers() {
    return this.outputRows()
      .map((row) => row.querySelector("[data-field=identifier]")?.value || "")
      .map((id) => this.normalizeId(id))
      .filter((id) => id.length > 0);
  }

  normalizeId(id) {
    return String(id || "")
      .trim()
      .toUpperCase();
  }

  outputRows() {
    return Array.from(
      this.outputListTarget.querySelectorAll("[data-output-row]"),
    );
  }

  serialize() {
    const outputs = this.outputRows().map((row) => {
      const output = {
        identifier: row.querySelector("[data-field=identifier]")?.value || "",
        net_weight_value:
          parseFloat(
            row.querySelector("[data-field=net_weight_value]")?.value,
          ) || 0,
        net_weight_unit:
          row.querySelector("[data-field=net_weight_unit]")?.value || "g",
      };
      if (this.photoKeyValue) {
        output[this.photoKeyValue] =
          row.querySelector(
            "[data-post-harvest--photo-upload-target='signedId']",
          )?.value || "";
      }
      return output;
    });

    this.outputContainersDataFieldTarget.value = JSON.stringify(outputs);
  }
}
